import {get as _get} from 'lodash';
import {withRouter} from 'react-router';

import DefaultForm from '@appComponents/DefaultForm';
import {getLink} from '@appComponents/Link';
import {convertToInt} from '@utils/helpers';
import {createGraphqlDebouncedFetch} from '@utils/graphqlDebounce';
import {GetTournamentCategoryDropdownDataFromSports} from '@graphql/sport/query';
import {GetCompetitorsForSpiideoForm} from '@graphql/spiideoCompetitorMapping/competitorTypeahead';

export const prepareSpiideoCompetitorMappingSubmitData = ({isEdit, id, data}) => {
    const payload = {
        competitor: convertToInt(data.competitor),
        spiideo_team_uuid: data.spiideo_team_uuid,
        sportradar_srt_url: data.sportradar_srt_url,
        ligr_srt_url: data.ligr_srt_url,
    };

    if (isEdit) {
        return {
            id: convertToInt(id),
            ...payload,
        };
    }

    return payload;
};

export class SpiideoCompetitorMappingForm extends DefaultForm {
    _indexLink = () => getLink('spiideoMappings.competitors');

    _loadCompetitorsDebounced = createGraphqlDebouncedFetch((event, data) => {
        const categoryId = _get(this.props, 'formValues.tournament_category', null);
        this._loadCompetitors(data.searchQuery, categoryId);
    });

    componentDidMount() {
        const navigateToIndex = () => this.props.history.push(this._indexLink());

        this.setCreateSuccessCallback(navigateToIndex);
        this.setUpdateSuccessCallback(navigateToIndex);
        this.setDeleteSuccessCallback(navigateToIndex);

        this.setOnChangeCallback({
            sport: (event) => this._onSportChange(event?.value ?? null),
            tournament_category: (event) => this._onCategoryChange(event?.value ?? null),
            competitor: (event) => this._onCompetitorChange(event),
        });

        this.setField('competitor', {
            onSearchChangeSelect: this._loadCompetitorsDebounced,
        });
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        const entity = _get(this.props, 'GraphQLEntityData.spiideoCompetitorMapping', null);

        if (entity && !this.state.dataLoaded) {
            this._populateEditFields(entity);
            this.setState(() => ({dataLoaded: true}));
        }
    }

    _populateEditFields = (entity) => {
        const sportId = _get(entity, 'competitor.sport.id', '');
        const categoryId = _get(entity, 'competitor.tournament_category.id', '');
        const categoryName = _get(entity, 'competitor.tournament_category.name', '');
        const competitorId = _get(entity, 'competitor.id', '');
        const competitorName = _get(entity, 'competitor.name', '');

        this.setField('sport', {
            disabled: false,
            hidden: false,
            defaultValue: sportId,
        });
        this.setField('tournament_category', {
            disabled: false,
            hidden: false,
            defaultValue: categoryId,
            options: categoryId ? [{
                key: categoryId,
                value: categoryId,
                text: categoryName,
            }] : [],
        });
        this._stashSelectedCompetitorOption({
            key: competitorId,
            value: competitorId,
            text: `${competitorName} (${competitorId})`,
        });
        this.setField('competitor', {
            disabled: false,
            hidden: false,
            defaultValue: competitorId,
            options: [this._selectedCompetitorOption],
        });

        if (sportId) {
            this._loadCategoryOptions(sportId, categoryId);
        }
    };

    _loadCategoryOptions = (sportId, selectedCategoryId = null) => {
        return this.runApolloRequest('query', {
            query: GetTournamentCategoryDropdownDataFromSports,
            variables: {sportIds: [convertToInt(sportId)]},
            fetchPolicy: 'network-only',
        }, {loadingFormFlag: false}).then((result) => {
            const categories = _get(result, 'data.tournamentCategories', []);

            this.setField('tournament_category', {
                hidden: false,
                disabled: false,
                options: categories.map((c) => ({
                    key: c.id,
                    value: c.id,
                    text: c.name,
                })),
                ...(selectedCategoryId ? {defaultValue: selectedCategoryId} : {}),
            });
        }).catch((error) => {
            this.setField('tournament_category', {
                loading: false,
                disabled: false,
            });
            this.setFormMessage(`${error.message}`);
        });
    };

    _onSportChange = (sportId) => {
        this._stashSelectedCompetitorOption(null);
        this.setField('tournament_category', {hidden: true, defaultValue: null, options: []});
        this.setField('competitor', {hidden: true, defaultValue: null, options: []});

        if (!sportId) {
            return;
        }

        this._loadCategoryOptions(sportId);
    };

    _onCategoryChange = (categoryId) => {
        this._stashSelectedCompetitorOption(null);
        this.setField('competitor', {hidden: true, defaultValue: null, options: []});

        if (categoryId) {
            this.setField('competitor', {
                hidden: false,
                options: [],
                disabled: false,
            });
        }
    };

    _onCompetitorChange = (data) => {
        const value = data?.value ?? null;

        if (!value) {
            this._stashSelectedCompetitorOption(null);

            return;
        }

        const match = (data.options || []).find((option) => String(option.value) === String(value));

        if (match) {
            this._stashSelectedCompetitorOption({
                key: match.key ?? value,
                value: match.value,
                text: match.text,
            });
        }
    };

    _stashSelectedCompetitorOption = (option) => {
        this._selectedCompetitorOption = option;
    };

    _mergeCompetitorOptions = (competitors) => {
        const options = competitors.map((competitor) => ({
            key: competitor.id,
            value: competitor.id,
            text: `${competitor.name} (${competitor.id})`,
        }));
        const selected = this._selectedCompetitorOption;

        if (selected && !options.some((option) => String(option.value) === String(selected.value))) {
            return [selected, ...options];
        }

        return options;
    };

    _loadCompetitors = (search, categoryId) => {
        if (!search || 3 > search.length || !categoryId) {
            return;
        }

        const sportValue = _get(this.props, 'formValues.sport', null);
        const includeId = convertToInt(_get(
            this.props,
            'GraphQLEntityData.spiideoCompetitorMapping.competitor.id',
            null
        ));

        this.setField('competitor', {loading: true, disabled: true});

        return this.runApolloRequest('query', {
            query: GetCompetitorsForSpiideoForm,
            variables: {
                search,
                sport: sportValue ? convertToInt(sportValue) : null,
                tournamentCategory: convertToInt(categoryId),
                includeId: includeId || null,
            },
            fetchPolicy: 'network-only',
        }, {loadingFormFlag: false}).then((result) => {
            const competitors = _get(result, 'data.competitorsForSpiideoMappingDropdown', []);

            this.setField('competitor', {
                loading: false,
                disabled: false,
                options: this._mergeCompetitorOptions(competitors),
            });
        }).catch((error) => {
            this.setField('competitor', {
                loading: false,
                disabled: false,
            });
            this.setFormMessage(`${error.message}`);
        });
    };

    prepareDataForSubmit = (data) => prepareSpiideoCompetitorMappingSubmitData({
        isEdit: Boolean(this.props.formParams?.id),
        id: this.props.formParams?.id,
        data,
    });

    renderErrors(errorData) {
        super.renderErrors(
            errorData,
            this.props.Model.label,
            this._indexLink(),
            {size: 'tiny'}
        );
    }

    renderCancelButton = () => null;

    renderDeleteButton = () => {
        if (!this.props.formParams?.id) {
            return null;
        }

        return super.renderDeleteButton();
    };
}

export default withRouter(SpiideoCompetitorMappingForm);
